import { useEffect, useMemo, useCallback } from 'react';
import api from '@/services/api';

/**
 * Lightweight per-visitor A/B assignment for SEO landing pages.
 *
 * The hook:
 *   1. Reads (or generates) a stable variant for the given experiment key
 *      and persists it in a first-party cookie for 30 days so repeat
 *      visitors always see the same copy.
 *   2. Fires a public "impression" event to /api/analytics/track-ab-event
 *      the first time a variant is shown in a browser tab.
 *   3. Returns a ``trackConversion(event)`` callback for CTA clicks / form
 *      submissions.
 *
 * Not intended for logged-in-only pages — for those, use the full A/B
 * framework in services/ab_testing.py which supports weighted variants,
 * multi-arm assignment, and per-user stickiness.
 */
export function useSeoAbVariant(experiment, variants = ['a', 'b']) {
  const cookieKey = `hx_seo_ab_${experiment}`;

  const variant = useMemo(() => {
    if (typeof document === 'undefined') return variants[0];
    const existing = document.cookie
      .split(';')
      .map((c) => c.trim())
      .find((c) => c.startsWith(`${cookieKey}=`));
    if (existing) {
      const v = existing.split('=')[1];
      if (variants.includes(v)) return v;
    }
    const picked = variants[Math.floor(Math.random() * variants.length)];
    // 30-day cookie, SameSite=Lax, no Secure so it works in preview HTTP too.
    document.cookie = `${cookieKey}=${picked}; Max-Age=${30 * 24 * 3600}; Path=/; SameSite=Lax`;
    return picked;
  }, [cookieKey, variants]);

  // Fire impression event once per tab. Wrapped in try/catch so a
  // network hiccup can never break the landing page render.
  useEffect(() => {
    const sessionKey = `seo_ab_impressed_${experiment}_${variant}`;
    if (typeof window === 'undefined' || sessionStorage.getItem(sessionKey)) return;
    sessionStorage.setItem(sessionKey, '1');
    api.post('/analytics/track-ab-event', {
      experiment,
      variant,
      event: 'impression',
      meta: { path: window.location.pathname },
    }).catch(() => {});
  }, [experiment, variant]);

  const trackConversion = useCallback((event = 'cta_click', meta = {}) => {
    if (typeof window === 'undefined') return;
    api.post('/analytics/track-ab-event', {
      experiment,
      variant,
      event,
      meta: { path: window.location.pathname, ...meta },
    }).catch(() => {});
  }, [experiment, variant]);

  return { variant, trackConversion };
}

export default useSeoAbVariant;
